import { useEffect, useMemo, useRef, useState } from 'react';
import { FileName, FolderPath } from '@/components/ui/FileName';
import { Upload, X, Check, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import { useUploadCenter } from '@/lib/upload-center';
import { useIsAdmin } from '@/hooks/use-scopes';
import { RAW_SUGGESTIONS, FOLDER_KINDS, guessRawCategory, addRawFolder, type AssetKind, type RawCategory } from '@/lib/queries';
import { cn } from '@/lib/cn';
import { droppedFolder, droppedFolders, filesFromDataTransfer } from '@/lib/dropped';

const KINDS: AssetKind[] = ['raw', 'edit', 'final', 'static', 'poster', 'reference', 'brief_doc'];
/** A creative code inside a filename: MS-4-2026-001, ms_4_2026_001, MS-4-2026-001-v2 */
const CODE_RX = /([A-Za-z]{2,4})[-_ ](\d+)[-_ ](\d{4})[-_ ](\d{3})/;

interface Target { id: string; code: string; title: string }
interface Row { file: File; creativeId: string; matched: boolean; category: RawCategory; state: 'queued' | 'uploading' | 'done' | 'failed'; pct: number; error?: string }

function codeOf(name: string): string | null {
  const m = CODE_RX.exec(name);
  return m ? `${m[1].toUpperCase()}-${m[2]}-${m[3]}-${m[4]}` : null;
}

/** Drop a whole shoot day. Files are routed to creatives by the code in their
 *  name; anything unmatched gets a picker. One kind per batch. */
/** The camera writes a small .XML beside every clip (C0198M01.XML). It is not a
 *  clip and nobody can open it, but it looks like one on the grid; six of them
 *  sat between five videos on one creative and read as "videos that do not
 *  open". They are left out here, and said so. */
// Aziz 2026-09-28/29: every file is kept, camera sidecars (.XML) and system files included.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function keepMedia(files: File[], _quiet = false): File[] {
  return files;
}

export default function BulkUploadDialog({ creatives, defaultKind, fixedCreativeId, initialFiles, folders, defaultFolder, onClose, onDone }: {
  creatives: Target[]; defaultKind?: AssetKind;
  /** set when opened from one creative's Assets card: no routing, just folders */
  fixedCreativeId?: string; initialFiles?: File[];
  /** the creative's own folders (shown first); the standard seven follow */
  folders?: string[]; defaultFolder?: string;
  onClose: () => void; onDone: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<AssetKind>(defaultKind ?? 'raw');
  const isAdmin = useIsAdmin();
  const [own, setOwn] = useState<string[]>(folders ?? []);
  const [batchCategory, setBatchCategory] = useState<RawCategory | ''>(defaultFolder && defaultFolder !== 'Any' ? defaultFolder : '');
  const [customName, setCustomName] = useState('');
  const folderOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const f of [...own, ...RAW_SUGGESTIONS]) if (!seen.has(f.toLowerCase())) seen.set(f.toLowerCase(), f);
    return Array.from(seen.values()).map((v) => ({ value: v, label: v, hint: own.includes(v) ? 'on this creative' : undefined }));
  }, [own]);
  // A file dropped inside a folder keeps that folder, nested under the open one:
  // drop "Day 1/Chair 2" while "Dr Sara" is open and it files into "Dr Sara/Day 1/Chair 2".
  const folderFor = (f: File, base: string): string => {
    const d = droppedFolder(f);
    if (d) return base && base !== 'Any' ? `${base}/${d}` : d;
    return base && base !== 'Any' ? base : guessRawCategory(f.name, f.type.startsWith('video/'), folders ?? []);
  };
  const [rows, setRows] = useState<Row[]>(() => keepMedia(initialFiles ?? [], true).map((f) => ({
    file: f, creativeId: fixedCreativeId ?? '', matched: !!fixedCreativeId,
    category: folderFor(f, defaultFolder ?? '') as RawCategory, state: 'queued', pct: 0,
  })));
  const dirRef = useRef<HTMLInputElement>(null);
  async function createFolder() {
    const n = customName.trim();
    if (!n || !fixedCreativeId) return;
    try { await addRawFolder(fixedCreativeId, n); setOwn((cur) => (cur.some((f) => f.toLowerCase() === n.toLowerCase()) ? cur : [...cur, n])); applyBatchCategory(n); setCustomName(''); toast.success(`Folder "${n}" ready`); }
    catch { toast.error('Could not create the folder'); }
  }
  const byCode = useMemo(() => new Map(creatives.map((c) => [c.code.toUpperCase(), c])), [creatives]);
  // Files dropped before the creative list arrived get matched once it does.
  useEffect(() => {
    if (byCode.size === 0) return;
    setRows((cur) => cur.map((r) => {
      if (r.creativeId || r.state !== 'queued') return r;
      const code = codeOf(r.file.name);
      const hit = code ? byCode.get(code) : undefined;
      return hit ? { ...r, creativeId: hit.id, matched: true } : r;
    }));
  }, [byCode]);

  function addFiles(files: FileList | File[] | null) {
    if (!files) return;
    const next: Row[] = keepMedia(Array.from(files)).map((f) => {
      const code = fixedCreativeId ? null : codeOf(f.name);
      const hit = code ? byCode.get(code) : undefined;
      return {
        file: f, creativeId: fixedCreativeId ?? hit?.id ?? '', matched: !!(fixedCreativeId || hit),
        category: folderFor(f, batchCategory) as RawCategory,
        state: 'queued', pct: 0,
      };
    });
    setRows((cur) => [...cur, ...next]);
  }
  function applyBatchCategory(c: RawCategory | '') {
    setBatchCategory(c);
    // a file that came with its own folder keeps it, nested under the new base
    if (c) setRows((cur) => cur.map((r) => (r.state === 'queued' ? { ...r, category: folderFor(r.file, c) as RawCategory } : r)));
  }

  // References, statics, posters and brief documents keep the folder they were dropped
  // or picked from too (2026-10-15); a loose file stays at the top level.
  const keepsFolder = kind !== 'raw' && FOLDER_KINDS.includes(kind);
  const folderOfRow = (r: Row): string | undefined => droppedFolder(r.file) ?? undefined;
  const ready = rows.filter((r) => r.creativeId && r.state === 'queued');
  const unrouted = rows.filter((r) => !r.creativeId).length;

  // Handing the files to the Upload Center rather than carrying them here is the
  // whole point: the queue lives above the router, so this window can close and the
  // crew can walk to the next creative and queue more while these are still moving.
  // One shared pool means that never adds parallel streams, only depth.
  const uploads = useUploadCenter();
  const navigate = useNavigate();
  // onDone() unmounts this dialog, but a double click can land twice before React
  // gets there, and that would queue every file a second time.
  const sent = useRef(false);
  function startAll() {
    if (sent.current) return;
    const items = rows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => r.creativeId && r.state === 'queued')
      .map(({ r }) => {
        const c = creatives.find((x) => x.id === r.creativeId);
        return {
          file: r.file,
          creativeId: r.creativeId,
          creativeCode: c?.code,
          creativeTitle: c?.title,
          kind,
          category: kind === 'raw' ? r.category : kind === 'edit' ? 'rough' : kind === 'final' ? 'final' : keepsFolder ? folderOfRow(r) : undefined,
        };
      });
    if (!items.length) return;
    sent.current = true;
    // folders that came from a drop are named on the creative too, so they show even before the first file lands
    if (kind === 'raw') {
      const pairs = new Map<string, [string, string]>();
      for (const it of items) if (it.category && it.category !== 'Any' && !own.some((f) => f.toLowerCase() === it.category!.toLowerCase())) pairs.set(`${it.creativeId}|${it.category.toLowerCase()}`, [it.creativeId, it.category]);
      void Promise.allSettled(Array.from(pairs.values()).map(([cid, cat]) => addRawFolder(cid, cat)));
    }
    uploads.enqueue(items);
    toast.success(`${items.length} file${items.length === 1 ? '' : 's'} queued`);
    onDone();
  }

  const totalBytes = rows.reduce((n, r) => n + r.file.size, 0);

  const options = creatives.map((c) => ({ value: c.id, label: `${c.code} ${c.title}` }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border border-slate-800 bg-bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-slate-800">
          <div>
            <h3 className="font-medium text-muted-strong">{fixedCreativeId ? 'Upload raw material' : 'Bulk upload'}</h3>
            <p className="text-xs text-muted mt-0.5">
              {fixedCreativeId
                ? 'File each clip in its folder: talking, treatments, mapping, before, after, reviews. The art director filters the shoot by these.'
                : <>Name files with the creative code (for example <span className="font-mono">MS-4-2026-001_take2.mp4</span>) and they route themselves. Pick the rest by hand.</>}
            </p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-muted-strong"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div
            className="border-2 border-dashed border-slate-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const dt = e.dataTransfer; void filesFromDataTransfer(dt).then((fs) => { addFiles(fs); const n = droppedFolders(fs).length; if (n) toast.success(`${n} folder${n === 1 ? '' : 's'} kept as folders`); }); }}
          >
            {isAdmin ? (
              <Select label="Upload every file as" value={kind} onChange={(v) => setKind(v as AssetKind)} options={KINDS.map((k) => ({ value: k, label: k.replace('_', ' ') }))} />
            ) : (
              // one rule since 2026-10-30 (Aziz): each file goes into the stage its piece is at
              <div data-testid="wizard-goes-to"><div className="text-xs text-muted mb-1.5">Files go to</div>
                <div className="min-h-10 flex items-center px-3 py-2 rounded-lg border border-slate-800 bg-bg text-sm text-muted-strong">The stage each piece is at</div></div>
            )}
            {kind === 'raw' && (
              <Select label="Put every raw in folder" value={batchCategory} onChange={(v) => applyBatchCategory(v as RawCategory | '')} placeholder="Guess from filename" searchable
                options={[{ value: '', label: 'Guess from filename' }, ...folderOptions]} />
            )}
            <div className={cn('flex flex-wrap items-center gap-2 min-w-0', kind === 'raw' ? '' : 'md:col-span-2')}>
              <Button icon={<Upload size={14} />} onClick={() => fileRef.current?.click()}>Pick files</Button>
              {(kind === 'raw' || keepsFolder) && <Button variant="secondary" onClick={() => dirRef.current?.click()} data-testid="wizard-pick-folder">Pick a folder</Button>}
              <span className="text-xs text-muted basis-full sm:basis-auto">or drop files, or whole folders with their subfolders, here</span>
              <input ref={fileRef} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.currentTarget.value = ''; }} />
              <input ref={dirRef} type="file" multiple hidden data-testid="wizard-folder-input" {...({ webkitdirectory: '' } as Record<string, string>)} onChange={(e) => { const fs = Array.from(e.target.files ?? []); e.currentTarget.value = ''; addFiles(fs); const n = droppedFolders(fs).length; if (n) toast.success(`${n} folder${n === 1 ? '' : 's'} kept as folders`); }} />
            </div>
          </div>

          {kind === 'raw' && fixedCreativeId && (
            <div className="flex items-center gap-2">
              <input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="New folder name, for example Dr Diana chair 2"
                onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); }}
                className="flex-1 h-9 rounded-lg border border-slate-800 bg-bg px-3 text-xs text-muted-strong focus:outline-none focus:border-accent-400" data-testid="wizard-new-folder" />
              <Button size="sm" variant="secondary" onClick={createFolder} disabled={!customName.trim()}>Create folder</Button>
            </div>
          )}
          {rows.length > 0 && (
            <div className="space-y-1.5">
              {rows.map((r, i) => (
                <div key={i} className={cn('grid gap-3 items-center rounded-lg border px-3 py-2',
                  kind === 'raw' && fixedCreativeId ? 'grid-cols-[1fr_minmax(180px,220px)_auto]' : kind === 'raw' ? 'grid-cols-[1fr_minmax(200px,1fr)_minmax(150px,180px)_auto]' : 'grid-cols-[1fr_minmax(220px,1fr)_auto]',
                  r.state === 'failed' ? 'border-red-500/40' : r.creativeId ? 'border-slate-800' : 'border-amber-500/40')}>
                  <div className="min-w-0">
                    <FileName name={r.file.name} className="block text-xs text-muted-strong truncate" />
                    <div className="text-[10px] text-muted">
                      {(r.file.size / 1048576).toFixed(1)} MB{fixedCreativeId ? '' : r.matched ? ' · matched by code' : r.creativeId ? ' · picked by hand' : ' · no code in the name, pick a creative'}
                      {keepsFolder && folderOfRow(r) && <span data-testid="wizard-row-folder"> · folder <FolderPath path={folderOfRow(r)} /></span>}
                    </div>
                  </div>
                  {!fixedCreativeId && (
                    <Select
                      value={r.creativeId}
                      onChange={(v) => setRows((cur) => cur.map((x, j) => (j === i ? { ...x, creativeId: v, matched: false } : x)))}
                      searchable size="sm" placeholder="Pick a creative..."
                      options={options}
                      disabled={r.state !== 'queued'}
                    />
                  )}
                  {kind === 'raw' && (
                    <Select
                      value={r.category}
                      onChange={(v) => setRows((cur) => cur.map((x, j) => (j === i ? { ...x, category: v as RawCategory } : x)))}
                      size="sm" options={folderOptions.some((o) => o.value === r.category) ? folderOptions : [{ value: r.category, label: r.category }, ...folderOptions]} searchable
                      disabled={r.state !== 'queued'}
                    />
                  )}
                  <div className="w-24 text-right text-[11px]">
                    {r.state === 'queued' && (
                      <button onClick={() => setRows((cur) => cur.filter((_, j) => j !== i))} className="text-muted hover:text-red-300">remove</button>
                    )}
                    {r.state === 'uploading' && <span className="text-accent-400">{r.pct}%</span>}
                    {r.state === 'done' && <span className="text-emerald-400 inline-flex items-center gap-1"><Check size={12} /> done</span>}
                    {r.state === 'failed' && <span className="text-red-300 inline-flex items-center gap-1" title={r.error}><AlertTriangle size={12} /> failed</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-5 border-t border-slate-800">
          <span className="text-xs text-muted">
            {rows.length === 0 ? 'No files yet.' : `${ready.length} ready${unrouted ? `, ${unrouted} still need a creative` : ''}`}
            {totalBytes > 0 && ` · ${(totalBytes / 1073741824).toFixed(2)} GB`}
          </span>
          <div className="flex items-center gap-2">
            {uploads.jobs.length > 0 && (
              <Button variant="ghost" onClick={() => { onClose(); navigate('/uploads'); }}>Upload Center</Button>
            )}
            <Button variant="ghost" onClick={onClose}>{rows.length ? 'Cancel' : 'Close'}</Button>
            <Button icon={<Upload size={14} />} onClick={startAll} disabled={ready.length === 0} data-testid="start-upload">
              {isAdmin ? <>Upload {ready.length} as {kind.replace('_', ' ')}</> : <>Upload {ready.length}</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
